// src/pages/Products.jsx
import React, { useEffect, useState, useCallback, useRef } from 'react';
import api from '../api';
import ProductCard from '../components/ProductCard';
import FireworksShow from '../components/FireworksShow';

// Firecracker animation constants (vote popup)
const FIRECRACKER_COLORS = [
  '#FFC107', '#FF5722', '#F44336', '#4CAF50', '#2196F3', '#9C27B0', '#FFFFFF', '#FFD700',
];
const POPUP_DURATION = 5000;
const BURST_INTERVAL = 250;

const getRandom = (min, max) => Math.random() * (max - min) + min;

/* GLOBAL KEYFRAMES for vote-burst particles + theme hooks */
if (typeof document !== 'undefined' && !document.getElementById('sparkExplode-keyframes')) {
  const styleSheet = document.createElement('style');
  styleSheet.id = 'sparkExplode-keyframes';
  styleSheet.innerHTML = `
    @keyframes sparkExplode {
      0% { transform: translate(0, 0) scale(0.5); opacity: 1; filter: brightness(1.5); }
      50% { opacity: 1; filter: brightness(1); }
      100% { transform: translate(var(--end-x), var(--end-y)) scale(0); opacity: 0; filter: brightness(0.5); }
    }
    @keyframes groundSpinner {
      0% { transform: translate(0, 0) scale(0.5) rotate(0deg); opacity: 1; filter: brightness(1); }
      50% { transform: translate(var(--end-x), var(--end-y)) scale(0.8) rotate(var(--angle)); opacity: 1; filter: brightness(2); }
      100% { transform: translate(var(--end-x), var(--end-y)) scale(0.2) rotate(calc(var(--angle) * 2)); opacity: 0; filter: brightness(0.5); }
    }
    .firecracker-particle { position: fixed; pointer-events: none; border-radius: 50%; z-index: 9999; will-change: transform, opacity; mix-blend-mode: screen; }
  `;
  document.head.appendChild(styleSheet);
}

export default function Products({ deadline }) {
  const [products, setProducts] = useState([]);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSuccess, setShowSuccess] = useState(false);

  // vote-burst timers
  const burstIntervalRef = useRef(null);
  const popupTimeoutRef = useRef(null);
  const firecrackerContainerRef = useRef(null);

  // canvas fireworks
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const rocketTimerRef = useRef(null);
  const fountainTimerRef = useRef(null);
  const wheelTimerRef = useRef(null);

  const rockets = useRef([]);
  const particles = useRef([]);
  const fountains = useRef([]);
  const wheels = useRef([]);

  /* Create container for vote-burst DOM particles */
  useEffect(() => {
    let container = document.getElementById('firecracker-container');
    if (!container) {
      container = document.createElement('div');
      container.id = 'firecracker-container';
      container.style.position = 'fixed';
      container.style.left = '0';
      container.style.top = '0';
      container.style.width = '100%';
      container.style.height = '100%';
      container.style.pointerEvents = 'none';
      container.style.overflow = 'hidden';
      container.style.zIndex = '9998';
      document.body.appendChild(container);
    }
    firecrackerContainerRef.current = container;

    return () => {
      if (container && container.parentNode) container.parentNode.removeChild(container);
      firecrackerContainerRef.current = null;
    };
  }, []);

  

  /* Canvas FIREWORKS: rockets, fountains, ground wheels */
  useEffect(() => {
    // Guard against React 18 StrictMode double-mount
    if (rafRef.current) return;

    try {
      const canvas = canvasRef.current;
      if (!canvas) return;

      // Ensure correct layering & visibility
      Object.assign(canvas.style, {
        position: 'fixed',
        inset: '0',
        width: '100vw',
        height: '100vh',
        zIndex: '10',
        pointerEvents: 'none',
      });

      const ctx = canvas.getContext('2d', { alpha: true });

      // Robust DPR resize
      const resize = () => {
        const dpr = Math.max(1, window.devicePixelRatio || 1);
        const cssW = window.innerWidth;
        const cssH = window.innerHeight;
        canvas.width  = Math.round(cssW * dpr);
        canvas.height = Math.round(cssH * dpr);
        canvas.style.width  = cssW + 'px';
        canvas.style.height = cssH + 'px';
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      };
      resize();
      window.addEventListener('resize', resize);

      const COLORS = ['#ffcc33', '#ff8844', '#ff4444', '#ffd966', '#fff1a8', '#ffb347', '#ffe680', '#ffffff'];
      const GRAVITY = 0.06;
      const DRAG = 0.985;

      function spawnRocket() {
        const x = Math.random() * window.innerWidth * 0.8 + window.innerWidth * 0.1;
        const targetY = window.innerHeight * (0.15 + Math.random() * 0.25);
        const vx = (Math.random() - 0.5) * 1.2;
        const vy = -(7.5 + Math.random() * 2.2);
        rockets.current.push({
          x, y: window.innerHeight + 10, vx, vy, targetY,
          trail: [], color: COLORS[(Math.random() * COLORS.length) | 0],
        });
      }

      function spawnFountain() {
        const count = 1 + (Math.random() < 0.4 ? 1 : 0);
        for (let i = 0; i < count; i++) {
          const x = window.innerWidth * (0.15 + Math.random() * 0.7);
          const baseY = window.innerHeight - 20;
          fountains.current.push({ x, y: baseY, life: 600 + (Math.random() * 600) | 0, hue: Math.random() * 30 + 30, tick: 0 });
        }
      }

      function spawnWheel() {
        const x = window.innerWidth * (0.1 + Math.random() * 0.8);
        const y = window.innerHeight - 30 - Math.random() * 20;
        wheels.current.push({ x, y, life: 500 + (Math.random() * 500) | 0, angle: Math.random() * Math.PI * 2, speed: 0.3 + Math.random() * 0.5, color: COLORS[(Math.random() * COLORS.length) | 0] });
      }

      function explode(x, y, color) {
        const count = 70 + (Math.random() * 50) | 0;
        for (let i = 0; i < count; i++) {
          const angle = Math.random() * Math.PI * 2;
          const speed = 2.8 + Math.random() * 5.2;
          particles.current.push({ kind: 'burst', x, y, vx: Math.cos(angle) * speed, vy: Math.sin(angle) * speed, life: 70 + (Math.random() * 50) | 0, color, size: 1.8 + Math.random() * 3.2, flicker: true });
        }
        for (let i = 0; i < 30; i++) {
          particles.current.push({ kind: 'burst', x, y, vx: (Math.random() - 0.5) * 1.7, vy: (Math.random() - 0.5) * 1.7, life: 28 + (Math.random() * 22) | 0, color: '#ffffff', size: 1.2 + Math.random() * 1.8, flicker: true });
        }
      }

      function emitFountain(f) {
        const sprays = 16 + (Math.random() * 10) | 0;
        for (let i = 0; i < sprays; i++) {
          const angle = (-Math.PI / 2) + getRandom(-0.45, 0.45);
          const speed = 2.5 + Math.random() * 2.5;
          const c = `hsl(${f.hue + getRandom(-10,10)}, 90%, ${60 + Math.random()*20}%)`;
          particles.current.push({ kind: 'fountain', x: f.x, y: f.y, vx: Math.cos(angle) * speed * 0.8, vy: Math.sin(angle) * speed * 1.2, life: 45 + (Math.random() * 25) | 0, color: c, size: 1 + Math.random() * 2.4, flicker: true });
        }
        particles.current.push({ kind: 'glow', x: f.x + getRandom(-4,4), y: f.y + getRandom(-2,2), vx: 0, vy: -0.2, life: 20, color: 'rgba(255,180,80,0.6)', size: 2.2, flicker: true });
      }

      function emitWheel(w) {
        const emits = 20;
        for (let i = 0; i < emits; i++) {
          const angle = w.angle + (i / emits) * Math.PI * 2;
          const speed = 2.2 + Math.random() * 2.0;
          particles.current.push({ kind: 'wheel', x: w.x, y: w.y, vx: Math.cos(angle) * speed, vy: Math.sin(angle) * speed, life: 30 + (Math.random() * 25) | 0, color: w.color, size: 1 + Math.random() * 2.0, flicker: true });
        }
        particles.current.push({ kind: 'glow', x: w.x, y: w.y, vx: 0, vy: 0, life: 25, color: 'rgba(255,200,120,0.7)', size: 2.5, flicker: true });
      }

      function drawRocket(r) {
        r.trail.push({ x: r.x, y: r.y });
        if (r.trail.length > 16) r.trail.shift();
        const lw = 2.5;
        for (let i = 0; i < r.trail.length - 1; i++) {
          const a = r.trail[i], b = r.trail[i + 1];
          const alpha = 0.10 * i;
          // draw thin glow segments
          const old = ctx.globalAlpha;
          ctx.globalAlpha = alpha;
          ctx.strokeStyle = 'rgba(255,210,140,1)';
          ctx.lineWidth = lw;
          ctx.beginPath();
          ctx.moveTo(a.x, a.y);
          ctx.lineTo(b.x, b.y);
          ctx.stroke();
          ctx.globalAlpha = old;
        }
        ctx.fillStyle = r.color;
        ctx.shadowBlur = 18;
        ctx.shadowColor = r.color;
        ctx.beginPath();
        ctx.arc(r.x, r.y, 3.2, 0, Math.PI * 2);
        ctx.fill();
        ctx.shadowBlur = 0;
      }

      function drawParticle(p) {
        p.x += p.vx;
        p.y += p.vy;

        if (p.kind === 'wheel') {
          p.vx *= DRAG; p.vy *= DRAG; p.vy += GRAVITY * 0.02;
        } else if (p.kind === 'fountain') {
          p.vx *= DRAG * 0.98; p.vy = p.vy * (DRAG * 0.97) + GRAVITY * 0.03;
        } else {
          p.vx *= DRAG; p.vy = p.vy * DRAG + GRAVITY;
        }

        p.life--;

        const alpha = Math.max(0, Math.min(1, p.life / 28));
        const glow = p.flicker ? (0.85 + Math.random() * 0.5) : 1;
        ctx.shadowBlur = 12;
        ctx.shadowColor = p.color;
        ctx.fillStyle = p.color;
        ctx.globalAlpha = alpha * glow;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();

        ctx.shadowBlur = 0;
        ctx.globalAlpha = 1;
        return p.life > 0;
      }

      let frames = 0;
     // src/pages/Products.jsx (Enhanced clear function)
      function clear() {
        ctx.save();
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        // Use a low-opacity fill with your background color to create a fading trail effect
        ctx.fillStyle = 'rgba(13, 26, 38, 0.15)'; // Matches --color-background: #0d1a26
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.restore();
      }
      function tick() {
        frames++;
        if (frames % 120 === 0) console.log('[FW] tick alive', frames);

        clear();

        rockets.current = rockets.current.filter(r => {
          r.x += r.vx; r.y += r.vy; r.vy += GRAVITY * 0.25;
          drawRocket(r);
          if (r.y <= r.targetY || r.vy > -0.4) { explode(r.x, r.y, r.color); return false; }
          return r.y < window.innerHeight + 60;
        });

        fountains.current = fountains.current.filter(f => {
          f.tick++; if (f.tick % 3 === 0) emitFountain(f);
          f.life--;
          ctx.fillStyle = 'rgba(255,180,90,0.2)';
          ctx.beginPath(); ctx.arc(f.x, f.y + 2, 3, 0, Math.PI * 2); ctx.fill();
          return f.life > 0;
        });

        wheels.current = wheels.current.filter(w => {
          w.angle += w.speed; emitWheel(w); w.life--;
          ctx.fillStyle = 'rgba(255,220,120,0.25)';
          ctx.beginPath(); ctx.arc(w.x, w.y, 2.5, 0, Math.PI * 2); ctx.fill();
          return w.life > 0;
        });

        particles.current = particles.current.filter(drawParticle);
        rafRef.current = requestAnimationFrame(tick);
      }

      // Spawn cadence with guards
      if (!rocketTimerRef.current) {
        rocketTimerRef.current = setInterval(() => {
          spawnRocket();
          if (Math.random() < 0.6) spawnRocket();
        }, 600);
      }
      if (!fountainTimerRef.current) fountainTimerRef.current = setInterval(spawnFountain, 2500);
      if (!wheelTimerRef.current) {
        wheelTimerRef.current = setInterval(() => {
          spawnWheel();
          if (Math.random() < 0.8) spawnWheel();
        }, 3500);
      }

      if (!rafRef.current) tick();

      return () => {
        window.removeEventListener('resize', resize);
        if (rocketTimerRef.current) { clearInterval(rocketTimerRef.current); rocketTimerRef.current = null; }
        if (fountainTimerRef.current) { clearInterval(fountainTimerRef.current); fountainTimerRef.current = null; }
        if (wheelTimerRef.current) { clearInterval(wheelTimerRef.current); wheelTimerRef.current = null; }
        if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
        particles.current = []; rockets.current = []; fountains.current = []; wheels.current = [];
      };
    } catch (e) {
      console.error('[FW] startup error:', e);
    }
  }, []);

  /* vote success DOM burst (kept) */
  const createFirecrackerExplosion = useCallback(() => {
    const container = firecrackerContainerRef.current || document.getElementById('firecracker-container');
    if (!container) return;

    const startX = getRandom(window.innerWidth * 0.1, window.innerWidth * 0.9);
    let startY = getRandom(window.innerHeight * 0.2, window.innerHeight * 0.8);
    const explosionType = Math.floor(getRandom(0, 4));

    let numParticles, distanceMin, distanceMax, durationMin, durationMax, keyframeName, sizeMin, sizeMax;

    switch (explosionType) {
      case 0: numParticles = Math.floor(getRandom(40, 60)); distanceMin = 100; distanceMax = 300; durationMin = 1.0; durationMax = 1.8; keyframeName = 'sparkExplode'; sizeMin = 6; sizeMax = 14; break;
      case 1: numParticles = Math.floor(getRandom(25, 40)); distanceMin = 200; distanceMax = 450; durationMin = 0.8; durationMax = 1.2; keyframeName = 'sparkExplode'; sizeMin = 4; sizeMax = 8; break;
      case 2: numParticles = Math.floor(getRandom(40, 70)); distanceMin = 50; distanceMax = 150; durationMin = 1.5; durationMax = 2.5; keyframeName = 'sparkExplode'; sizeMin = 3; sizeMax = 7; break;
      case 3: numParticles = Math.floor(getRandom(20, 30)); startY = getRandom(window.innerHeight * 0.7, window.innerHeight * 0.9); distanceMin = 20; distanceMax = 80; durationMin = 0.5; durationMax = 1.0; keyframeName = 'groundSpinner'; sizeMin = 2; sizeMax = 6; break;
      default: numParticles = 50; distanceMin = 100; distanceMax = 300; durationMin = 1.0; durationMax = 1.8; keyframeName = 'sparkExplode'; sizeMin = 6; sizeMax = 14;
    }

    for (let i = 0; i < numParticles; i++) {
      const particle = document.createElement('div');
      particle.className = 'firecracker-particle';
      const size = getRandom(sizeMin, sizeMax);
      particle.style.width = `${size}px`;
      particle.style.height = `${size}px`;
      particle.style.background = FIRECRACKER_COLORS[Math.floor(Math.random() * FIRECRACKER_COLORS.length)];
      particle.style.left = `${startX}px`;
      particle.style.top = `${startY}px`;

      const angle = getRandom(0, 2 * Math.PI);
      const distance = getRandom(distanceMin, distanceMax);
      const endX = distance * Math.cos(angle);
      const endY = distance * Math.sin(angle);

      const duration = getRandom(durationMin, durationMax);
      const delay = getRandom(0, 0.2);

      particle.style.setProperty('--end-x', `${endX}px`);
      particle.style.setProperty('--end-y', `${endY}px`);
      particle.style.setProperty('--angle', `${getRandom(-360, 360)}deg`);
      particle.style.animation = `${keyframeName} ${duration}s ${delay}s forwards ease-out`;

      container.appendChild(particle);
      particle.onanimationend = () => particle.remove();
    }
  }, []);

  // load products + me
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const pRes = await api.get('/products');
      setProducts(Array.isArray(pRes.data) ? pRes.data : []);
      const token = localStorage.getItem('token');
      if (token) {
        try { const meRes = await api.get('/auth/me'); setMe(meRes?.data || null); }
        catch { setMe(null); }
      } else setMe(null);
    } catch (err) {
      console.error('Products load error', err); setProducts([]); setMe(null);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const onUserChange = () => load();
    window.addEventListener('userChanged', onUserChange);
    return () => window.removeEventListener('userChanged', onUserChange);
  }, [load]);

  useEffect(() => {
    return () => {
      if (burstIntervalRef.current) { clearInterval(burstIntervalRef.current); burstIntervalRef.current = null; }
      if (popupTimeoutRef.current) { clearTimeout(popupTimeoutRef.current); popupTimeoutRef.current = null; }
    };
  }, []);

  const onVoted = useCallback(() => {
    load();
    setShowSuccess(true);
    if (!burstIntervalRef.current) {
      createFirecrackerExplosion();
      burstIntervalRef.current = setInterval(() => { createFirecrackerExplosion(); }, BURST_INTERVAL);
    }
    if (popupTimeoutRef.current) clearTimeout(popupTimeoutRef.current);
    popupTimeoutRef.current = setTimeout(() => {
      setShowSuccess(false);
      if (burstIntervalRef.current) { clearInterval(burstIntervalRef.current); burstIntervalRef.current = null; }
      popupTimeoutRef.current = null;
    }, POPUP_DURATION);
  }, [createFirecrackerExplosion, load]);

  if (loading) {
    return (<div className="page"><h2>Loading…</h2></div>);
  }

  const now = new Date();
  const deadlineDate = deadline ? new Date(deadline) : null;
  const votingOpen = !deadlineDate || deadlineDate > now;

  return (
    <div className="diwali-stage">
      {/* ambient glow + fireworks canvas are handled by CSS + this canvas */}
      <div className="diwali-bokeh" aria-hidden />
      <canvas id="diwali-fireworks-canvas" ref={canvasRef} />

      <div className="page diwali-content">
        <h1 style={{ textAlign: 'center', color: '#ffb347', textShadow: '0 0 10px rgba(255,204,0,.8),0 0 30px rgba(255,102,0,.5)', letterSpacing: '.5px', fontSize: '20px' }}>
          Diwali Gift Voting
        </h1>

        {me && me.votedProduct && (
          <div className="info" style={{ boxShadow: '0 0 20px rgba(255,140,0,0.2) inset' }}>
            You already voted for: <strong>{me.votedProduct.name ?? me.votedProduct}</strong>
          </div>
        )}

        <div className="grid">
          {products.length ? products.map(p => (
            <ProductCard
              key={p._id || p.id || p.name}
              product={p}
              me={me}
              onVoted={onVoted}
              votingOpen={votingOpen}
            />
          )) : (
            <div style={{ padding: 20 }}>No products available yet.</div>
          )}
        </div>

        {!localStorage.getItem('token') && <div className="info">Please login/signup to vote.</div>}

        {showSuccess && (
          <div className="vote-success-popup" role="status" aria-live="polite">
            <h2>🎉 Thank you for your vote! 🙏</h2>
            <p>Wishing you a Happy Diwali!</p>
          </div>
        )}
      </div>

      <div className="diwali-stage">
  <div className="diwali-bokeh" aria-hidden />

  {/* Auto-running rockets → burst + side fountains + ground spinners */}
  <FireworksShow active={votingOpen} intensity={1.0} />

  <div className="page diwali-content">
    {/* ...rest of your UI... */}
  </div>
</div>
    </div>



  );
}



